import styled from "styled-components";

import { SipFrequency } from "@ctypes";
import { Translations, i18n } from "@i18n";
import { Card } from "@routes/DashboardRoute/Home/styles";
import { home } from "@routes/DashboardRoute/Home/tokens";
import { calculateProjectedProgression, formatCurrency, goalRate } from "@utils";
import { GoalScenario, PathChartProps } from "./types";

const CHART_W = 1176;
const CHART_H = 220;
const PLOT_TOP = 30;
const PLOT_BASE = 204;
const POINTS = 11;

const Shell = styled(Card)`
  border-radius: 22px;
  background: linear-gradient(160deg, rgba(26, 34, 52, 0.72) 0%, rgba(16, 20, 32, 0.86) 100%);
  padding: 26px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #f2efe9;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 40px;
`;

const HeaderLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #e09b7c;
`;

const TargetHeadline = styled.div`
  font-family: ${home.serif};
  font-size: 38px;
  line-height: 0.96;
  letter-spacing: -1px;
`;

const HeaderNote = styled.div`
  font-size: 13px;
  color: rgba(242, 239, 233, 0.48);
`;

const TodayBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
  text-align: right;
  margin-left: auto;

  .label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: rgba(242, 239, 233, 0.42);
  }

  .value {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.6px;
  }
`;

const TargetPill = styled.div`
  position: absolute;
  left: 0;
  height: 24px;
  display: flex;
  align-items: center;
  padding: 0 11px;
  border-radius: 999px;
  background: rgba(14, 18, 26, 0.8);
  border: 1px solid rgba(242, 239, 233, 0.22);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: rgba(242, 239, 233, 0.86);
`;

const YearAxis = styled.div`
  position: relative;
  height: 34px;
  padding-top: 12px;
  font-size: 12px;

  .start {
    position: absolute;
    left: 0;
    top: 12px;
    color: rgba(242, 239, 233, 0.4);
  }

  .mid {
    position: absolute;
    left: 50%;
    top: 12px;
    transform: translateX(-50%);
    color: rgba(242, 239, 233, 0.3);
  }

  .end {
    position: absolute;
    right: 0;
    top: 12px;
    font-weight: 500;
    color: rgba(242, 239, 233, 0.62);
  }
`;

const Tiles = styled.div`
  display: flex;
  align-items: stretch;
  gap: 18px;
  padding-top: 22px;
  border-top: 1px solid ${home.line08};

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const Tile = styled.div<{ $accent?: boolean }>`
  flex-grow: 1;
  flex-basis: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid
    ${({ $accent }) => ($accent ? "rgba(224,155,124,0.34)" : "rgba(242,239,233,0.10)")};
  background: ${({ $accent }) => ($accent ? "rgba(224,155,124,0.10)" : "rgba(242,239,233,0.03)")};

  .swatch-row {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(242, 239, 233, 0.7);
  }

  .value {
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.5px;
    color: ${({ $accent }) => ($accent ? "#E09B7C" : home.ink)};
  }

  .note {
    font-size: 12px;
    color: rgba(242, 239, 233, 0.48);
  }
`;

const Swatch = styled.span<{ $kind: "accent" | "dashed" | "flat" }>`
  width: 20px;
  height: 2px;
  display: block;
  background: ${({ $kind }) =>
    $kind === "accent"
      ? "#E09B7C"
      : $kind === "dashed"
        ? "repeating-linear-gradient(90deg, rgba(242,239,233,0.55) 0 5px, transparent 5px 10px)"
        : "rgba(242,239,233,0.34)"};
`;

const SplitBar = styled.div`
  display: flex;
  gap: 3px;
  height: 6px;
  margin-top: 2px;

  .invested {
    background: rgba(242, 239, 233, 0.55);
    border-radius: 999px 2px 2px 999px;
  }

  .growth {
    background: #e09b7c;
    border-radius: 2px 999px 999px 2px;
  }
`;

const strokeFor = (kind: GoalScenario["kind"]) =>
  kind === "accent"
    ? "#E09B7C"
    : kind === "dashed"
      ? "rgba(242,239,233,0.55)"
      : "rgba(242,239,233,0.34)";

export const PathChart = ({
  goal,
  scenarios,
  target,
  current,
  targetYear,
  eyebrow,
  note,
  splitTile,
}: PathChartProps) => {
  const nowYear = new Date().getFullYear();
  const years = Math.max(targetYear - nowYear, 1);
  const rate = goalRate(goal);
  const balance = current;

  const max = Math.max(target, ...scenarios.map((s) => s.projected)) * 1.06 || 1;
  const y = (amount: number) => PLOT_BASE - (amount / max) * (PLOT_BASE - PLOT_TOP);
  const x = (i: number) => (i / (POINTS - 1)) * CHART_W;

  const seriesFor = (annualDeposit: number) => {
    const progression = calculateProjectedProgression({
      amount: annualDeposit,
      recurringDepositType: SipFrequency.Annually,
      currentAge: 0,
      projectedAge: years,
      contributeAfter18: goal.contributionsAfter18 ?? true,
      initialBalance: balance,
      multiplier: rate + 1,
    });

    return Array.from({ length: POINTS }, (_, i) => {
      const position = (i * (progression.length - 1)) / (POINTS - 1);
      const lower = Math.floor(position);
      const upper = Math.min(lower + 1, progression.length - 1);
      const fraction = position - lower;
      const amount =
        (progression[lower]?.amount ?? 0) * (1 - fraction) +
        (progression[upper]?.amount ?? 0) * fraction;
      return { x: x(i), y: y(amount), amount };
    });
  };

  const paths = scenarios.map((scenario) => ({
    scenario,
    points: seriesFor(scenario.annualDeposit),
  }));

  const toLine = (points: Array<{ x: number; y: number }>) =>
    `M${points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L")}`;

  const reaching = paths.find((p) => p.scenario.kind === "accent");
  const reachingArea = reaching
    ? `${toLine(reaching.points)} L${CHART_W},${PLOT_BASE} L0,${PLOT_BASE} Z`
    : null;

  const targetY = y(target);

  // Where the reaching path crosses the target line.
  const crossing = (() => {
    if (!reaching) return null;
    for (let i = 1; i < reaching.points.length; i++) {
      const a = reaching.points[i - 1];
      const b = reaching.points[i];
      if (a.amount < target && b.amount >= target) {
        const t = (target - a.amount) / (b.amount - a.amount);
        return { x: a.x + (b.x - a.x) * t };
      }
    }
    return reaching.points[reaching.points.length - 1].amount >= target ? { x: CHART_W } : null;
  })();

  return (
    <Shell>
      <HeaderRow>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <HeaderLabel>{eyebrow}</HeaderLabel>
          <TargetHeadline>{formatCurrency(target)}</TargetHeadline>
          <HeaderNote>{note}</HeaderNote>
        </div>
        <TodayBlock>
          <span className="label">{i18n.t(Translations.HomeToday)}</span>
          <span className="value">{formatCurrency(current, true)}</span>
        </TodayBlock>
      </HeaderRow>

      <div>
        <div style={{ position: "relative", height: CHART_H }}>
          <svg
            width="100%"
            height={CHART_H}
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            <defs>
              <linearGradient id="fmGoalReach" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E09B7C" stopOpacity="0.50" />
                <stop offset="55%" stopColor="#E09B7C" stopOpacity="0.16" />
                <stop offset="100%" stopColor="#E09B7C" stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {reachingArea && <path d={reachingArea} fill="url(#fmGoalReach)" />}

            {paths.map(({ scenario, points }) => (
              <path
                key={scenario.key}
                d={toLine(points)}
                fill="none"
                stroke={strokeFor(scenario.kind)}
                strokeWidth={scenario.kind === "accent" ? 2.5 : 1.8}
                strokeDasharray={scenario.kind === "dashed" ? "5 5" : undefined}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}

            <line
              x1="0"
              y1={targetY}
              x2={CHART_W}
              y2={targetY}
              stroke="rgba(242,239,233,0.46)"
              strokeWidth="1.5"
              strokeDasharray="6 5"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1="0"
              y1={PLOT_BASE}
              x2={CHART_W}
              y2={PLOT_BASE}
              stroke="rgba(242,239,233,0.16)"
              strokeWidth="1"
            />
          </svg>

          <TargetPill style={{ top: (targetY / CHART_H) * 100 - 12 + "%" }}>
            {i18n.t(Translations.GoalBreakdownRouteTargetPill, {
              target: formatCurrency(target),
            })}
          </TargetPill>

          {crossing && (
            <div
              style={{
                position: "absolute",
                left: `${(crossing.x / CHART_W) * 100}%`,
                top: `${(targetY / CHART_H) * 100}%`,
                width: 11,
                height: 11,
                margin: "-5.5px 0 0 -5.5px",
                borderRadius: 999,
                background: "#E09B7C",
                border: "2px solid #10141C",
              }}
            />
          )}
        </div>

        <YearAxis>
          <span className="start">{nowYear}</span>
          <span className="mid">{Math.round((nowYear + targetYear) / 2)}</span>
          <span className="end">{targetYear}</span>
        </YearAxis>
      </div>

      <Tiles>
        {scenarios.map((scenario) => (
          <Tile key={scenario.key} $accent={scenario.kind === "accent"}>
            <div className="swatch-row">
              <Swatch $kind={scenario.kind} />
              {scenario.label}
            </div>
            <div className="value">{formatCurrency(scenario.projected)}</div>
            <div className="note">{scenario.note}</div>
          </Tile>
        ))}

        {splitTile && (
          <Tile>
            <div className="swatch-row">{splitTile.title}</div>
            <SplitBar>
              <div className="invested" style={{ width: `${splitTile.investedPct}%` }} />
              <div className="growth" style={{ width: `${100 - splitTile.investedPct}%` }} />
            </SplitBar>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="note">{splitTile.investedLabel}</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#E09B7C",
                  marginLeft: "auto",
                }}
              >
                {splitTile.growthLabel}
              </span>
            </div>
          </Tile>
        )}
      </Tiles>
    </Shell>
  );
};
